import { Box, Stack, useMantineTheme } from '@mantine/core';
import { Icon, Text } from '@repo/foundations';
import { DataTable } from '@repo/mantine-datatable';
import { map } from 'lodash-es';
import { useMemo } from 'react';
import type { IDatabuilderJob } from '../../../api';
import { snakeCaseToTitleCase } from '../../../utils/shared.utils';
import { ErrorLogs } from '../../ErrorLogs/ErrorLogs';
import { TableV2Loader } from '../../TableV2';
import {
	FOOTER_HEIGHT,
	ROW_HEIGHT,
	useTableStyles,
} from '../../TableV2/TableV2.styles';

const RECORDS_PER_PAGE = 10;

type SummaryRow = {
	label: string;
	value: number | string;
};

export function ExtractionSummary(props: { job: IDatabuilderJob }) {
	const theme = useMantineTheme();
	const { classes: tableClasses } = useTableStyles({
		hideCheckbox: true,
		noBorder: true,
		disableSelectedDisplay: true,
		withHover: true,
	});

	const { job } = props;

	const columns = useMemo(
		() => [
			{
				accessor: 'label',
				title: 'Entity type',
			},
			{
				accessor: 'value',
				title: 'Number of entities',
			},
		],
		[]
	);

	// Check if num_entities_by_entity_type exists and has any entries
	const hasEntityTypes =
		job.summary?.num_entities_by_entity_type &&
		Object.keys(job.summary.num_entities_by_entity_type).length > 0;

	// Check if there's CSV-related data
	const hasCSVData =
		job.summary?.num_rows !== undefined ||
		job.summary?.num_invalid_rows !== undefined;

	// Check if sync completed successfully but no new/changed entities were found
	// Only show "no changes" message if there's no entity data AND no CSV data
	// Don't show for failed, terminated, or timed out jobs
	const syncCompletedWithNoChanges =
		job.completed &&
		!job.failed &&
		!job.terminated &&
		!job.timed_out &&
		job.summary?.num_entities_by_entity_type !== undefined &&
		!hasEntityTypes &&
		!hasCSVData;

	// If no summary data and not a successful sync with no changes, don't render
	if (
		!job.summary ||
		(!hasEntityTypes && !hasCSVData && !syncCompletedWithNoChanges)
	) {
		return null;
	}

	const summaryRows: SummaryRow[] = [];

	if (job.summary.num_rows !== undefined) {
		summaryRows.push({
			label: 'Number of rows in CSV',
			value: job.summary.num_rows,
		});
	}

	if (job.summary.num_invalid_rows !== undefined) {
		summaryRows.push({
			label: 'Number of invalid rows in CSV',
			value: job.summary.num_invalid_rows,
		});
	}

	if (hasEntityTypes) {
		map(job.summary.num_entities_by_entity_type, (numEntities, entityType) => {
			summaryRows.push({
				label: `${snakeCaseToTitleCase(entityType)}s`,
				value: numEntities,
			});
		});
	}

	const hasCSVError =
		job.summary.invalid_rows_and_validation_errors !== undefined &&
		job.summary.invalid_rows_and_validation_errors.length > 0;

	return (
		<Box my={theme.spacing.lg}>
			<Text size="md" weight="bold" pb="md">
				Sync summary
			</Text>
			{syncCompletedWithNoChanges ? (
				<Stack spacing="xs">
					<Text size="sm" color="text/secondary/default">
						Sync completed successfully. No new or changed entities were
						detected during this incremental sync.
					</Text>
				</Stack>
			) : (
				<DataTable<SummaryRow>
					borderRadius="md"
					withBorder
					classNames={tableClasses}
					maxHeight={RECORDS_PER_PAGE * ROW_HEIGHT + FOOTER_HEIGHT}
					height={summaryRows.length * ROW_HEIGHT + FOOTER_HEIGHT}
					noRecordsIcon={<Icon size="lg" name="search" />}
					noRecordsText={'No stages found'}
					records={summaryRows}
					columns={columns}
					customLoader={<TableV2Loader />}
				/>
			)}
			{hasCSVError && (
				<ErrorLogs
					title="Error samples"
					logs={[
						JSON.stringify(
							job.summary.invalid_rows_and_validation_errors,
							null,
							2
						),
					]}
				/>
			)}
		</Box>
	);
}
